import React from "react";
//Limitless imports
import { useNotificationContext } from "@limitless/common/hooks/notification/NotificationContext";
import { log as pilog } from "@limitless/common/utils";
import { fetchExpertMessage, claimExpertMessage, addExpertDialogue, addExpertAttachment, INITIAL_MESSAGE_RESOURCES, calculateMessageState, CLAIMED_FOR_REVIEW, DISAGREED, CLAIMED_FOR_RESPONSE_DURING_REVIEW, SILENT_CLAIM_TIMEOUT, unclaimExpertMessage, increaseMessageClaimTimeOut, useKnowledgeLibraryContext, getCustomerData, calculateShowCustomerConfirmation, findMessageResources, CLAIMED_FOR_REVIEW_DETAILED } from "../../../portal";
import { useResources } from "@limitless/common/hooks";
import { recordException } from "@limitless/analytics/hooks/AnalyticsHelper";
import { useExpertMessageResponseContext } from "../expert-message-response/ExpertMessageResponseContext";
import { useExpertTaskListContext } from "../expert-task-list/ExpertTaskListContext";
import { useExpertSuggestedAnswersContext } from "../expert-suggested-answers/ExpertSuggestedAnswersContext";
import { useExpertProfileContext } from "../expert-profile/ExpertProfileContext";
import { fetchMessageAIReview } from "../../../portal";
export const INITIAL_MESSAGE_STATE = {
  dialogues: [],
  extraInfo: [],
  showKnowledge: false,
  knowledgeBaseIsActive: false,
  loading: false,
  hasAddedDialogue: false,
  hasFlaggedConversationComplete: false,
  hasExpertTouched: false,
  isLastCustomerDialogue: false,
  readOnlyTags: [],
  editableTags: [],
  expertAuthors: {},
  hasError: false,
  new: false,
  addingDialogue: false
};
const RESET_RESOURCES = "reset-message-resources";
const REPLACE_RESOURCES = "replace-message-resources";
const DUPE_DIALOGUE_LENGTH = 50;
const messageResourceReducer = (state, action) => {
  switch (action.type) {
    case REPLACE_RESOURCES:
      return {
        ...INITIAL_MESSAGE_RESOURCES,
        ...action.payload
      };
    case RESET_RESOURCES:
      return INITIAL_MESSAGE_RESOURCES;
    default:
      throw new Error(`Unsupported message resource action type ${action.tyoe}`);
  }
};
export const useMessage = () => {
  var _messageResources$rea3, _messageResources$rea4;
  const log = pilog.getLogger("MessageReasonCode loadReasonCode");
  // Use State to keep the values
  const [message, setMessage] = React.useState(INITIAL_MESSAGE_STATE);
  const [messageResources, dispatchMessageResource] = React.useReducer(messageResourceReducer, INITIAL_MESSAGE_RESOURCES);
  const [dialogueError, setDialogueError] = React.useState(null);
  const [dialogueNote, setDialogueNote] = React.useState({});
  const [corruptUserSession, setCorruptUserSession] = React.useState(false);
  const [customerSecureData, setCustomerSecureData] = React.useState([]);
  const [responseCodeError, setResponseCodeError] = React.useState("");
  const {
    addFailureNotification,
    addSuccessNotification,
    addInfoNotification
  } = useNotificationContext();
  const {
    loadMessageKnowledgeLibrary,
    clearSelectedArticle,
    clearSelectedLibrary
  } = useKnowledgeLibraryContext();
  const [passiveQrEventFlag, setPassiveQrEventFlag] = React.useState(false);

  //pull the clear methods from the other message related contexts
  const {
    clearMessageResponse
  } = useExpertMessageResponseContext();
  const {
    addTaskListRecord,
    updateTaskListRecord,
    removeTaskListRecordAndBlackList,
    removeTasklistRecord
  } = useExpertTaskListContext();
  const {
    clearSAState,
    dismissAnswers
  } = useExpertSuggestedAnswersContext();
  const {
    profile: profileAPI
  } = useExpertProfileContext();
  const profileAPIRef = React.useRef(profileAPI);
  React.useEffect(() => {
    profileAPIRef.current = profileAPI;
  }, [profileAPI]);
  const isButtonVisible = React.useMemo(() => {
    var _messageResources$rea, _messageResources$rea2;
    return !responseCodeError && (messageResources === null || messageResources === void 0 ? void 0 : (_messageResources$rea = messageResources.reasonCodes) === null || _messageResources$rea === void 0 ? void 0 : (_messageResources$rea2 = _messageResources$rea.codes) === null || _messageResources$rea2 === void 0 ? void 0 : _messageResources$rea2.response) <= 0;
  }, [messageResources === null || messageResources === void 0 ? void 0 : (_messageResources$rea3 = messageResources.reasonCodes) === null || _messageResources$rea3 === void 0 ? void 0 : (_messageResources$rea4 = _messageResources$rea3.codes) === null || _messageResources$rea4 === void 0 ? void 0 : _messageResources$rea4.response, responseCodeError]);
  const toggleMessageLoading = () => {
    setMessage({
      ...message,
      loading: !message.loading
    });
  };
  const stopLoading = () => {
    setMessage({
      ...message,
      loading: false
    });
  };
  const {
    mergeUILabels,
    updateUILabels
  } = useResources();
  const getMessageResource = (messageInbox, profile, callback) => {
    dispatchMessageResource({
      type: RESET_RESOURCES
    });
    callback(messageResources, profileAPIRef.current, messageInbox);
  };
  const selectMessage = (messageInbox, profile, callback) => {
    setMessage({
      ...INITIAL_MESSAGE_STATE,
      loading: true,
      hasError: false
    });
    dispatchMessageResource({
      type: RESET_RESOURCES
    });
    fetchExpertMessage(profileAPIRef.current, messageInbox, messageResources, null, {}, (message, messageResources, messageLabels) => {
      if (message && !message.hasError) {
        setMessageAndResources(message, messageResources, profileAPIRef.current);
        if (typeof callback === "function") {
          callback(message, messageResources, profileAPIRef.current);
        }
        if (messageLabels) {
          if (messageLabels.message) {
            //we have message level labels, lets merge them with the current ones
            mergeUILabels(messageLabels.message);
          } else {
            var _messageLabels$origin, _messageLabels$origin2;
            //if we don't find a ui labels file for this message, reload the original labels file
            //only update the label if we had originals
            //we might not have origianls if a locale has no ui_labels
            updateUILabels((_messageLabels$origin = (_messageLabels$origin2 = messageLabels.original) === null || _messageLabels$origin2 === void 0 ? void 0 : _messageLabels$origin2.content) !== null && _messageLabels$origin !== void 0 ? _messageLabels$origin : {});
          }
        }
      } else {
        //we have an error, reset the message and inform the user
        setMessage({
          ...INITIAL_MESSAGE_STATE,
          hasError: true
        });
        addFailureNotification("Failed to retrieve task. Please try again.");
      }
    });
  };
  const refreshMessage = (messageInbox, profile) => {
    fetchExpertMessage(profileAPIRef.current, messageInbox.inbox, messageResources, null, {}, newMessage => {
      setMessage({
        ...message,
        dialogues: newMessage === null || newMessage === void 0 ? void 0 : newMessage.dialogues
      });
    });
  };
  const onPassedMessage = (messageInbox, profile, callback) => {
    fetchExpertMessage(profileAPIRef.current, messageInbox, messageResources, null, {}, newMessage => {
      if (newMessage.inbox) {
        setMessage({
          ...message,
          dialogues: newMessage === null || newMessage === void 0 ? void 0 : newMessage.dialogues
        });
        callback(newMessage);
      } else {
        addFailureNotification("Failed to pass task");
        callback();
      }
    });
  };
  const setMessageAndResources = (message, messageResources, profile) => {
    dispatchMessageResource({
      type: REPLACE_RESOURCES,
      payload: messageResources
    });
    //if this message has a knowledge base, load it
    if (message.knowledgeBaseIsActive) {
      loadMessageKnowledgeLibrary(message);
    }
    const expertActionProps = {};
    if (message.dialogues.length > 1) {
      if (message.dialogues[message.dialogues.length - 1].origin === "expert") {
        const isSameExpert = message.dialogues[message.dialogues.length - 1].userId === profileAPIRef.current.userId;
        expertActionProps.hasExpertTouched = isSameExpert;
        expertActionProps.hasAddedDialogue = isSameExpert;
      }
    }
    setMessage({
      ...INITIAL_MESSAGE_STATE,
      ...message,
      ...expertActionProps
    });
    const notes = {};
    message.dialogues.map(d => {
      var _d$contentModerated;
      if ((_d$contentModerated = d.contentModerated) !== null && _d$contentModerated !== void 0 && _d$contentModerated.reason) {
        notes[d.dialogueId] = true;
      }
    });
    setDialogueNote(notes);
  };
  const fetchReasonCodes = (message, messageResources) => {
    const options = {
      currentMessageResources: messageResources,
      appName: "expert_web_app",
      blackListResources: []
    };
    findMessageResources(profileAPIRef.current, message, messageResources, options, (message, messageResources) => {
      var _messageResources$rea5, _messageResources$rea6, _messageResources$rea7;
      if ((messageResources === null || messageResources === void 0 ? void 0 : (_messageResources$rea5 = messageResources.reasonCodes) === null || _messageResources$rea5 === void 0 ? void 0 : (_messageResources$rea6 = _messageResources$rea5.codes) === null || _messageResources$rea6 === void 0 ? void 0 : (_messageResources$rea7 = _messageResources$rea6.response) === null || _messageResources$rea7 === void 0 ? void 0 : _messageResources$rea7.length) > 0) {
        setMessageAndResources(message, messageResources, profileAPIRef.current);
      } else {
        var _messageResources$rea8, _messageResources$rea9;
        log.debug("CODE_LOAD_ERROR", messageResources === null || messageResources === void 0 ? void 0 : (_messageResources$rea8 = messageResources.reasonCodes) === null || _messageResources$rea8 === void 0 ? void 0 : (_messageResources$rea9 = _messageResources$rea8.codes) === null || _messageResources$rea9 === void 0 ? void 0 : _messageResources$rea9.response);
        setResponseCodeError("Unable to load response code");
      }
    });
  };
  const fetchAIReview = (messageId, callback) => {
    fetchMessageAIReview(messageId, result => {
      if (!result.hasError) {
        const {
          aiReview,
          aiRespond
        } = result;
        setMessage({
          ...message,
          aiReview: aiReview || aiRespond,
          loading: false
        });
        if (typeof callback === "function") {
          callback(aiReview || aiRespond);
        }
      } else {
        // Handle error scenario
        setMessage({
          ...message,
          loading: false
        });
        addFailureNotification("Failed to fetch AI review. Please try again.");
        if (typeof callback === "function") {
          callback(null);
        }
      }
    });
  };
  const onClaimMessage = (message, messageResources, profile, callback) => {
    toggleMessageLoading();
    claimExpertMessage(profileAPIRef.current, message, messageResources, claimedMessage => {
      if (!claimedMessage.hasError) {
        if (claimedMessage.claimedBy !== profileAPIRef.current.userName) {
          //uh oh, we got a claim but it wasn't ours!
          //this happens when people have EWA open in different tabs
          //due to AWS cognito sdk rubbishness
          removeTasklistRecord(message.messageId);
          setCorruptUserSession(true);
          clearMessage();
        } else {
          fetchExpertMessage(profile, message, messageResources, null, {}, (newMessage, messageResources) => {
            if (newMessage && (!newMessage.dialogues || newMessage.dialogues.length === 0)) {
              clearMessage();
              addInfoNotification("This task is no longer available as the dialogue has been removed.");
              removeTasklistRecord(message.messageId);
              callback({
                hasError: true
              });
              return;
            }
            const newMessageState = calculateMessageState(profileAPIRef.current, newMessage, messageResources);
            if (newMessage && !newMessage.hasError) {
              setMessageAndResources(newMessageState, messageResources, profileAPIRef.current);
            }
            setMessage({
              ...newMessageState,
              ...newMessage,
              loading: false
            });
            callback(newMessage);
          });
        }
      } else {
        //we have an error, turn off loading and inform the user
        clearMessage();
        if (JSON.stringify(claimedMessage === null || claimedMessage === void 0 ? void 0 : claimedMessage.error).indexOf("claimed by another") !== -1) {
          addInfoNotification("Task claimed by another ambassador.");
        } else if (JSON.stringify(claimedMessage === null || claimedMessage === void 0 ? void 0 : claimedMessage.error).indexOf("does not exist for the expert") !== -1) {
          addInfoNotification("Task no longer available.");
        } else {
          addFailureNotification("Failed to claim task");
        }
        callback({
          hasError: true
        });
      }
    });
  };
  const claimMessage = () => {
    toggleMessageLoading();
    claimExpertMessage(profileAPIRef.current, message, messageResources, claimedMessage => {
      if (!claimedMessage.hasError) {
        if (claimedMessage.claimedBy !== profileAPIRef.current.userName) {
          //uh oh, we got a claim but it wasn't ours!
          //this happens when people have EWA open in different tabs
          //due to AWS cognito sdk rubbishness
          removeTasklistRecord(message.messageId);
          setCorruptUserSession(true);
          clearMessage();
        } else {
          if (!message.dialogues || message.dialogues.length === 0) {
            clearMessage();
            addInfoNotification("This task is no longer available as the dialogue has been removed.");
            removeTasklistRecord(message.messageId);
            return;
          }
          const newMessageState = calculateMessageState(profileAPIRef.current, message, messageResources);
          setMessage({
            ...newMessageState,
            ...claimedMessage,
            loading: false
          });
        }
      } else {
        //we have an error, turn off loading and inform the user
        clearMessage();
        if (JSON.stringify(claimedMessage === null || claimedMessage === void 0 ? void 0 : claimedMessage.error).indexOf("claimed by another") !== -1) {
          addInfoNotification("Task claimed by another ambassador.");
        } else if (JSON.stringify(claimedMessage === null || claimedMessage === void 0 ? void 0 : claimedMessage.error).indexOf("does not exist for the expert") !== -1) {
          addInfoNotification("Task no longer available.");
        } else {
          addFailureNotification("Failed to claim task");
        }
        removeTasklistRecord(message.messageId);
      }
    });
  };
  const claimTimeoutIncrement = callback => {
    increaseMessageClaimTimeOut(message, res => {
      if (!res.hasError) {
        setMessage({
          ...message,
          ...res
        });
        if (typeof callback === "function") {
          callback(res);
        }
      } else {
        // the message will be unclaim if hasError = true
      }
    });
  };
  const getCustomerSecureData = () => {
    getCustomerData(message, profileAPIRef.current, secureData => {
      setCustomerSecureData(secureData.data);
    });
  };
  const addDialogue = (body, saState, options = {}) => {
    const previousHasExpertTouched = message.hasExpertTouched;
    const {
      tempDialogueId,
      newMessage,
      payload
    } = addTempDialogue(body, saState, null, {
      hasExpertTouched: true,
      addingDialogue: true,
      ...(options.isAIDraftSelected ? {
        isAIDraftSelected: options.isAIDraftSelected,
        feedback: options.feedback,
        lastCustomerDialogueId: options.lastCustomerDialogueId
      } : {})
    });
    addExpertDialogue(newMessage, payload, (dialogueResponse, custConfirmationState) => {
      if (!dialogueResponse.hasError) {
        if (dialogueResponse.contentModerated.reason) {
          setDialogueNote({
            ...dialogueNote,
            [dialogueResponse.dialogueId]: true
          });
        }
        newMessage.custConfirmationState = custConfirmationState;
        replaceTempDialogue(tempDialogueId, {
          ...newMessage,
          hasAddedDialogue: true,
          isLastCustomerDialogue: false,
          hasExpertTouched: true,
          addingDialogue: false
        }, dialogueResponse);
        setDialogueError(null);
      } else {
        if (dialogueResponse.hasUserLostClaim) {
          processDialogueFailure(dialogueResponse, body);
        } else {
          setMessage({
            ...message,
            addingDialogue: false,
            hasExpertTouched: previousHasExpertTouched
          });
          processDialogueFailure(dialogueResponse, body);
          setDialogueError(tempDialogueId);
        }
      }
    });
  };
  const addTempDialogue = (body, saState, dialogueProps, messageProps, releaseClaim, newMessageState) => {
    var _profileAPIRef$curren;
    const tempDialogueId = Math.random();
    const dialogue = {
      dialogueId: tempDialogueId,
      body,
      author: (_profileAPIRef$curren = profileAPIRef.current) === null || _profileAPIRef$curren === void 0 ? void 0 : _profileAPIRef$curren.author,
      origin: "expert",
      type: "standard",
      created: new Date().getTime(),
      userId: profileAPIRef.current.userId,
      contentModerated: {},
      ...dialogueProps
    };
    const expertAuthors = {
      ...message.expertAuthors
    };
    if (message.conversationState.showExpertThumbnail) {
      const expertInfo = {
        answerCount: profileAPIRef.current.answerCount,
        rating: profileAPIRef.current.custRating,
        thumbnail: profileAPIRef.current.picture || null
      };
      expertAuthors[profileAPIRef.current.userId] = expertInfo;
    }
    let newMessage = {
      ...message,
      ...messageProps,
      expertAuthors
    };
    if (releaseClaim) {
      newMessage.inbox.claimed = false;
    }
    newMessage.dialogues.push(dialogue);
    if (newMessageState) {
      newMessage = changeMessageState(newMessageState, newMessage);
    } else {
      setMessage(newMessage);
    }
    clearMessageResponse();
    clearSelectedArticle();
    const payload = {
      body
    };
    if (saState !== null && saState !== void 0 && saState.selectedAnswer) {
      payload.referenceDialogueId = saState.selectedAnswer.dialogueId;
      dismissAnswers();
    }
    if ((messageProps === null || messageProps === void 0 ? void 0 : messageProps.isAIDraftSelected) === true) {
      payload.isAIDraftSelected = messageProps.isAIDraftSelected;
      payload.feedback = messageProps.feedback;
      payload.lastCustomerDialogueId = messageProps.lastCustomerDialogueId;
    }
    return {
      newMessage,
      tempDialogueId,
      payload
    };
  };
  const resubmitDialogue = (dialogue, callback) => {
    addExpertDialogue(message, {
      body: dialogue.body
    }, (dialogueResponse, custConfirmationState) => {
      if (!dialogueResponse.hasError) {
        dialogueResponse.hasError = false;
        replaceTempDialogue(dialogue.tempDialogueId, {
          ...message,
          custConfirmationState,
          hasAddedDialogue: true,
          isLastCustomerDialogue: false,
          hasExpertTouched: true
        }, dialogueResponse);
        callback(true);
      } else {
        processDialogueFailure(dialogueResponse, dialogue.body);
        callback(false);
      }
    });
  };
  const processDialogueFailure = (dialogueResponse, body) => {
    if (dialogueResponse.hasUserLostClaim) {
      var _navigator, _navigator$clipboard;
      //expert must have missed a web socket event
      changeMessageState(SILENT_CLAIM_TIMEOUT);
      //attempt to save the last body to the
      if (typeof ((_navigator = navigator) === null || _navigator === void 0 ? void 0 : (_navigator$clipboard = _navigator.clipboard) === null || _navigator$clipboard === void 0 ? void 0 : _navigator$clipboard.writeText) === "function") {
        navigator.clipboard.writeText(body);
      }
      recordException(dialogueResponse.errorMessage);
    } else {
      addFailureNotification("Failed to reply to task. Please try again.");
    }
  };
  const replaceTempDialogue = (tempDialogueId, newMessage, dialogue) => {
    const tempDialogueIndex = newMessage.dialogues.findIndex(dialogue => dialogue.dialogueId === tempDialogueId);
    newMessage.dialogues.splice(tempDialogueIndex, 1, dialogue);
    // For Conversation complete text when new expert dialogue is added
    if (newMessage.custConfirmationUIControl === "always") {
      calculateShowCustomerConfirmation(newMessage);
    }
    setMessage(newMessage);
  };
  const addNewDialogue = (dialogue, messageProps = {}, state = null) => {
    var _dialogue$attachments;
    const newMessage = {
      ...message,
      ...messageProps
    };
    const existingDialogue = newMessage.dialogues.find(message => {
      return message.dialogueId === dialogue.dialogueId;
    });
    // Update dialogue in case of image scan
    if ((dialogue === null || dialogue === void 0 ? void 0 : (_dialogue$attachments = dialogue.attachments) === null || _dialogue$attachments === void 0 ? void 0 : _dialogue$attachments.length) > 0 && existingDialogue) {
      newMessage.dialogues.forEach(newDialogue => {
        if (newDialogue.dialogueId === dialogue.dialogueId && newDialogue.attachments.length > 0) {
          newDialogue.attachments[0] = {
            ...newDialogue.attachments[0],
            ...dialogue.attachments[0]
          };
        }
      });
    } else {
      newMessage.dialogues.push(dialogue);
    }
    changeMessageState(state, newMessage);
  };
  const addAttachment = (file, caption, uploadAttachmentOverride) => {
    toggleMessageLoading();
    addExpertAttachment(file, caption, profileAPIRef.current, message, messageResources, uploadAttachmentOverride, newMessage => {
      if (!newMessage.hasError) {
        const lastDialogue = newMessage.dialogues[newMessage.dialogues.length - 1];
        if (lastDialogue.contentModerated.reason) {
          setDialogueNote({
            ...dialogueNote,
            [lastDialogue.dialogueId]: true
          });
        }
        addSuccessNotification("Attachment added successfully");
        setMessage({
          ...message,
          ...newMessage,
          loading: false,
          hasAddedDialogue: true,
          hasExpertTouched: true,
          isLastCustomerDialogue: false
        });
      } else {
        addFailureNotification("Failed to add attachment. Please try again.");
        setMessage({
          ...message,
          loading: false
        });
      }
    });
  };
  const switchToRespond = () => {
    changeMessageState(CLAIMED_FOR_RESPONSE_DURING_REVIEW);
  };
  const changeMessageState = (state, newMessage) => {
    //sometimes we want to use a newly calculated message to derive the next state.
    const newMessageState = calculateMessageState(profileAPIRef.current, newMessage ? newMessage : message, messageResources, state);
    setMessage(newMessageState);
    return newMessageState;
  };
  const unclaimMessage = removeFromInbox => {
    if (removeFromInbox) {
      //optimistically remove from the inbox
      removeTaskListRecordAndBlackList(message.messageId);
    } else {
      updateTaskListRecord({
        ...message.inbox,
        claimed: false
      });
    }
    unclaimExpertMessage(message, result => {
      if (result.hasError && removeFromInbox) {
        //if we couldn't unclaim, add it back to the inbox
        addTaskListRecord(message.inbox);
      }
      clearMessage();
    });
  };
  const clearMessage = () => {
    setMessage(INITIAL_MESSAGE_STATE);
    dispatchMessageResource({
      type: RESET_RESOURCES
    });
    setDialogueError(null);
    setDialogueNote({});
    clearMessageResponse();
    clearSAState();
    clearSelectedLibrary();
  };
  const clearMessageAndUnclaim = () => {
    unclaimMessage(true);
    clearMessage();
  };
  const finishEditingMessage = () => {
    clearMessageAndUnclaim();
  };
  const cancelEditingMessage = () => {
    unclaimMessage(false);
    // clearMessage();
  };
  const cancelEditingReviewMessage = () => {
    var _message$inbox, _message$inbox2;
    //if it's a review escalation flip back to Disagreed
    changeMessageState(((_message$inbox = message.inbox) === null || _message$inbox === void 0 ? void 0 : _message$inbox.type) === "reviewEscalation" ? DISAGREED ? ((_message$inbox2 = message.inbox) === null || _message$inbox2 === void 0 ? void 0 : _message$inbox2.type) === "reviewDetailed" : CLAIMED_FOR_REVIEW_DETAILED : CLAIMED_FOR_REVIEW);
  };
  const isDupeDialogue = body => {
    if ((body === null || body === void 0 ? void 0 : body.length) < DUPE_DIALOGUE_LENGTH) {
      return false;
    } else {
      const dupeDialogue = message.dialogues.find(d => d.body === body);
      if (dupeDialogue) {
        return true;
      }
    }
    return false;
  };

  // Make the context object:
  return {
    message,
    messageResources,
    selectMessage,
    getMessageResource,
    claimMessage,
    onPassedMessage,
    addDialogue,
    addNewDialogue,
    addAttachment,
    refreshMessage,
    clearMessage,
    clearMessageAndUnclaim,
    switchToRespond,
    toggleMessageLoading,
    setMessage,
    addTempDialogue,
    replaceTempDialogue,
    changeMessageState,
    setMessageAndResources,
    fetchReasonCodes,
    cancelEditingMessage,
    cancelEditingReviewMessage,
    finishEditingMessage,
    resubmitDialogue,
    dialogueError,
    setDialogueError,
    dialogueNote,
    claimTimeoutIncrement,
    stopLoading,
    corruptUserSession,
    setCorruptUserSession,
    getCustomerSecureData,
    customerSecureData,
    isDupeDialogue,
    responseCodeError,
    isButtonVisible,
    onClaimMessage,
    fetchAIReview,
    setPassiveQrEventFlag,
    passiveQrEventFlag
  };
};